/**
 * Server-driven gate for the self-service onboarding routes.
 *
 * Replaces the pre-launch localStorage override: the flow renders only when
 * `GET /api/waitlist/self-service/status/` reports `enabled`, and otherwise
 * falls through to the login redirect exactly as an unregistered route would.
 *
 * It also owns the two identity redirects the flow needs:
 *   - a signed-in user who already belongs to an org is bounced into the app
 *     (first-orgs-only — the same rule the backend `confirm` enforces) — unless
 *     that one org is their own self-service signup with an unfinished Stripe
 *     Checkout, in which case Stripe's cancel_url (`/get-started`) sends them
 *     back to the trial step to finish paying (`confirm` resumes the org);
 *   - a signed-in, org-less user on the create-account screen is sent straight
 *     into the wizard, and an anonymous visitor to a wizard step is sent back to
 *     the create-account screen (the steps need a session for suggestions and
 *     confirm).
 *
 * The decision itself is the pure `resolveGateState` in `../gateState.ts`.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { useSelfServiceStatus } from '@/lib/self-service/api';
import {
  isUnfinishedCheckout,
  resolveGateState,
} from '@/lib/self-service/gateState';

interface SelfServiceGateProps {
  requireAuth: boolean;
  children: ReactNode;
}

export function SelfServiceGate({
  requireAuth,
  children,
}: SelfServiceGateProps) {
  const status = useSelfServiceStatus();
  const { isAuthenticated } = useAuthInfo();
  const { availableOrgs, hasLoadedSuccessfully, error } = useOrg();

  const state = resolveGateState({
    enabled: status.data?.enabled,
    statusFailed: status.isError,
    isAuthenticated,
    orgsLoaded: hasLoadedSuccessfully,
    orgsFailed: error !== null,
    hasOrg: availableOrgs.length > 0,
    hasOnlyUnfinishedCheckout:
      availableOrgs.length === 1 && isUnfinishedCheckout(availableOrgs[0]),
    requireAuth,
  });

  switch (state.kind) {
    case 'loading':
      return (
        <div className='flex min-h-dvh items-center justify-center bg-[var(--color-bg-primary)]'>
          <LoadingIndicator size='md' />
        </div>
      );
    case 'redirect':
      return <Navigate to={state.to} replace />;
    case 'ready':
      return children;
  }
}
